import {ScreenLayout} from "@/components/layouts/page/ScreenLayout";
import TableLayout from "@/components/layouts/page/TableLayout";

import {useCallback, useEffect, useMemo, useState} from "react";

import {useDeepCompareEffect} from "@hooks/DeepCompareEffect";
import {usePopup} from "@layouts/popup/InfoPopup/PopupContext";
import {DeleteDataPopup} from "@popups/attentionPopups/DeleteDataPopup";
import {
  decrementTotalData,
  getProducts,
  setInactiveData,
  setPage,
  setSearch,
  setSelectedData,
  setSize,
  setSort,
  setTotalData,
  setTotalPages,
} from "@redux/slices/productSlices/tableSlice";
import {
  archiveProduct,
  deleteProduct,
  getAttachedScenarios,
  unArchiveProduct,
} from "@redux/slices/productSlices/viewSlice";
import {ControlButton} from "@ui/buttons/ControlButton";
import {TableButton} from "@ui/buttons/TableButton";
import {RootCheckbox} from "@ui/inputs/RootCheckbox";
import moment from "moment/min/moment-with-locales";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

export const ProductTablePage = () => {
  const {openPopup, closePopup} = usePopup();
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {demo} = useSelector((state) => state.common.candidate);

  const {
    inactiveData,
    data,
    page,
    size,
    sort,
    search,
    totalPages,
    totalData,
    status,
    selectedData,
  } = useSelector((state) => state.product.table);

  const [params, setParams] = useState({
    page: 1,
    size: 5,
    with_archived: inactiveData,
    sort,
  });

  const handleClickArchiveAll = useCallback(async () => {
    try {
      const results = await Promise.all(
        selectedData.map(
          (product) =>
            !product.archived_at && dispatch(archiveProduct({id: product.id})),
        ),
      );

      const successfulResults = results.filter((result) =>
        archiveProduct.fulfilled.match(result),
      );

      if (
        successfulResults.every((result) =>
          archiveProduct.fulfilled.match(result),
        )
      ) {
        const newParams = {
          page,
          size,
          with_archived: inactiveData,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        if (!inactiveData) {
          dispatch(setTotalData(totalData - successfulResults.length));
        }

        setParams(newParams);
        dispatch(getProducts(newParams));
      }
    } catch (e) {
      console.log("Error archiving all products: ", e);
    }
  }, [page, size, search, sort, inactiveData, totalData, selectedData]);

  const removeAllProducts = useCallback(async () => {
    try {
      const results = await Promise.all(
        selectedData.map((product) =>
          dispatch(deleteProduct({id: product?.id})),
        ),
      );

      if (results.every((result) => deleteProduct.fulfilled.match(result))) {
        const newParams = {
          page,
          size,
          with_archived: inactiveData,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        dispatch(setTotalData(totalData - results.length));
        setParams(newParams);
        dispatch(getProducts(newParams));
        closePopup();
      }
    } catch (e) {
      console.log("Error removing all products: ", e);
    }
  }, [page, size, search, sort, inactiveData, selectedData, totalData]);

  const handleClickUnarchiveAll = useCallback(async () => {
    try {
      const results = await Promise.all(
        selectedData.map(
          (product) =>
            product.archived_at && dispatch(unArchiveProduct({id: product.id})),
        ),
      );

      const successfulResults = results.filter((result) =>
        unArchiveProduct.fulfilled.match(result),
      );

      if (
        successfulResults.every((result) =>
          unArchiveProduct.fulfilled.match(result),
        )
      ) {
        const newParams = {
          page,
          size,
          with_archived: inactiveData,
          ...(search && {search}),
          ...(Object.keys(sort).length !== 0 && {sort}),
        };

        setParams(newParams);
        dispatch(getProducts(newParams));
      }
    } catch (e) {
      console.log("Error unarchiving all products: ", e);
    }
  }, [page, size, search, sort, inactiveData, selectedData]);

  const handleCheckFilter = () => {
    dispatch(setPage(1));
    dispatch(setInactiveData(!inactiveData));
  };

  const goToPage = (pageIndex) => {
    dispatch(setPage(pageIndex + 1));
  };

  const handleSelectSize = (option) => {
    dispatch(setSize(+option));
    dispatch(setPage(1));
    dispatch(setTotalPages(Math.ceil(totalData / +option)));
  };

  const handleSelectSort = (option) => {
    dispatch(setSort(option));
  };

  const handleSearch = useCallback(
    (value) => {
      clearTimeout(window.searchTimeout);

      window.searchTimeout = setTimeout(() => {
        dispatch(setSearch(value.toLowerCase()));
        dispatch(getProducts({page: 1, size, search: value}));
      }, 500);
    },
    [dispatch, size],
  );

  const handleClickDeleteAll = () => {
    openPopup(
      <DeleteDataPopup
        onClickNo={() => closePopup()}
        onClickYes={() => removeAllProducts()}
      />,
      {closeButton: false},
    );
  };

  const handleClickArchiveProduct = useCallback(
    async (item) => {
      try {
        const resultAction = await dispatch(archiveProduct({id: item.id}));

        if (archiveProduct.fulfilled.match(resultAction)) {
          const newParams = {
            page,
            size,
            with_archived: inactiveData,
            ...(search && {search}),
            ...(Object.keys(sort).length !== 0 && {sort}),
          };

          if (!inactiveData) {
            dispatch(decrementTotalData());
          }

          setParams(newParams);
          dispatch(getProducts(newParams));
        }
      } catch (e) {
        console.log("Error archiving product: ", e);
      }
    },
    [page, size, search, sort, inactiveData],
  );

  const handleClickUnarchiveProduct = useCallback(
    async (item) => {
      try {
        const resultAction = await dispatch(unArchiveProduct({id: item.id}));

        if (unArchiveProduct.fulfilled.match(resultAction)) {
          const newParams = {
            page,
            size,
            with_archived: inactiveData,
            ...(search && {search}),
            ...(Object.keys(sort).length !== 0 && {sort}),
          };

          setParams(newParams);
          dispatch(getProducts(newParams));
        }
      } catch (e) {
        console.log("Error unarchiving product: ", e);
      }
    },
    [page, size, search, sort, inactiveData],
  );

  const removeProduct = useCallback(
    async (id, without = false) => {
      try {
        const resultAction = await dispatch(deleteProduct({id, without}));

        if (deleteProduct.fulfilled.match(resultAction)) {
          const newParams = {
            page,
            size,
            with_archived: inactiveData,
            ...(search && {search}),
            ...(Object.keys(sort).length !== 0 && {sort}),
          };

          dispatch(decrementTotalData());
          setParams(newParams);
          dispatch(getProducts(newParams));
          closePopup();
        }
      } catch (e) {
        console.log("Error deleting product: ", e);
      }
    },
    [page, size, search, sort, inactiveData],
  );

  const handleClickDeleteProduct = async (item) => {
    openPopup(
      <DeleteDataPopup
        onClickYesFull={() => removeProduct(item.id, true)}
        onClickYes={() => removeProduct(item.id)}
        onClickNo={() => closePopup()}
        btnYesFull
        btnYes={t("button.yes_product")}
        btnNo={t("button.no_product")}
        title={t("popup.delete_data.title_product")}
        desc={t("popup.delete_data.subtitle_product")}
        getDesc={() => dispatch(getAttachedScenarios({id: item.id}))}
      />,
      {closeButton: false},
    );
  };

  const controls = [
    {
      id: 0,
      button: (
        <ControlButton
          color={"var(--green)"}
          txt={t("button.add")}
          link
        />
      ),
    },
    {
      id: 1,
      button: (
        <ControlButton
          color={"var(--red)"}
          txt={t("button.deactivate")}
          onClick={() => handleClickArchiveAll()}
          disabled={
            !selectedData ||
            selectedData.length <= 1 ||
            !selectedData.some((product) => !product.archived_at)
          }
        />
      ),
    },
  ];

  const advancedControls = [
    {
      id: 3,
      button: (
        <ControlButton
          color={"var(--green)"}
          txt={t("table.button.activate")}
          onClick={() => handleClickUnarchiveAll()}
          disabled={
            !selectedData ||
            selectedData.length <= 1 ||
            !selectedData.some((product) => product.archived_at) ||
            selectedData.some((product) => !product.archived_at)
          }
        />
      ),
    },
    {
      id: 4,
      button: (
        <ControlButton
          color={"var(--red)"}
          txt={t("table.button.delete")}
          onClick={() => handleClickDeleteAll()}
          disabled={
            !selectedData ||
            selectedData.length <= 1 ||
            !selectedData.some((product) => product.archived_at) ||
            selectedData.some((product) => !product.archived_at)
          }
        />
      ),
    },
  ];

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: t("table.name"),
        accessor: "name",
        sortable: true,
        width: 275,
      },
      {
        Header: t("table.product_line"),
        accessor: "product_line",
        sortable: true,
        width: 310,
      },
      {
        Header: t("table.status_files"),
        accessor: "state",
        sortable: true,
        Cell: ({value}) => {
          switch (value) {
            case "failed":
              return t("table.status_failed");
            case "pending":
              return t("table.status_pending");
            case "processing":
              return t("table.status_processing");
            case "completed":
              return t("table.status_completed");
            default:
              return value;
          }
        },
        width: 170,
      },
      {
        Header: t("table.status_questions"),
        accessor: "questions_state",
        sortable: true,
        Cell: ({value}) => {
          switch (value) {
            case "failed":
              return t("table.status_failed");
            case "pending":
              return t("table.status_pending");
            case "processing":
              return t("table.status_processing");
            case "completed":
              return t("table.status_completed");
            default:
              return value;
          }
        },
        width: 170,
      },
      {
        Header: t("table.created"),
        accessor: "created_at",
        sortable: true,
        Cell: ({value}) => (value ? moment(value).format("DD-MM-YYYY") : ""),
        width: 280,
      },
      {
        Header: t("table.actions"),
        Cell: ({row}) => (
          <div
            style={
              demo
                ? {
                    justifyContent: "flex-end",
                    marginLeft: "auto",
                    width: "fit-content",
                  }
                : {}
            }
            className={"flex gap-[8rem] phone:gap-[40rem]"}
          >
            <TableButton
              icon={"view"}
              color={"var(--orange)"}
              label={t("table.button.view")}
              action={"view"}
              item={row.original}
            />

            {!demo && (
              <>
                {!row.original.archived_at && (
                  <TableButton
                    icon={"edit"}
                    color={"var(--blueLight)"}
                    label={t("table.button.edit")}
                    action={"edit"}
                    item={row.original}
                  />
                )}

                {row.original.archived_at ? (
                  <>
                    <TableButton
                      icon={"activate"}
                      color={"var(--green)"}
                      label={t("table.button.activate")}
                      action={"activate"}
                      onClickAction={() =>
                        handleClickUnarchiveProduct(row.original)
                      }
                      item={row.original}
                    />

                    <TableButton
                      icon={"delete"}
                      color={"var(--red)"}
                      label={t("table.button.delete")}
                      action={"delete"}
                      onClickAction={() =>
                        handleClickDeleteProduct(row.original)
                      }
                      item={row.original}
                    />
                  </>
                ) : (
                  <TableButton
                    icon={"deactivate"}
                    color={"var(--red)"}
                    label={t("table.button.deactivate")}
                    action={"deactivate"}
                    onClickAction={() =>
                      handleClickArchiveProduct(row.original)
                    }
                    item={row.original}
                  />
                )}
              </>
            )}
          </div>
        ),
        width: 210,
      },
    ];

    if (!demo) {
      baseColumns.unshift({
        id: "selection",
        Header: ({getToggleAllRowsSelectedProps}) => (
          <div>
            <RootCheckbox {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({row}) => (
          <div>
            <RootCheckbox {...row.getToggleRowSelectedProps()} />
          </div>
        ),
        width: 50,
      });
    }

    return baseColumns;
  }, [t, demo, page, size, search, sort, inactiveData, data]);

  useEffect(() => {
    const newParams = {
      with_archived: inactiveData,
      ...(page > 0 ? {page} : {page: 1}),
      ...(size > 0 ? {size} : {size: 5}),
      ...(search && {search}),
      ...(Object.keys(sort).length !== 0 && {sort}),
    };

    setParams(newParams);
  }, [page, size, search, sort, inactiveData]);

  useDeepCompareEffect(() => {
    dispatch(getProducts(params));
  }, [params]);

  // calculate total pages when totalData changes
  useEffect(() => {
    dispatch(setTotalPages(Math.ceil(totalData / size)));
  }, [totalData, size, dispatch]);

  // calculate page when page > totalPages and data is empty
  useEffect(() => {
    if (page === totalPages + 1) {
      dispatch(setPage(Math.ceil(totalData / size)));
    }
  }, [totalPages, totalData, size, dispatch]);

  return (
    <ScreenLayout
      title={t("page.products.title")}
      controlsList={
        !demo && [...controls, ...(inactiveData ? advancedControls : [])]
      }
    >
      <TableLayout
        serverStatus={status}
        columns={columns}
        data={data}
        dataSort={sort}
        selectSort={handleSelectSort}
        dataSize={size}
        selectSize={handleSelectSize}
        searchPlaceholder={t("table.search_products")}
        searchValue={search}
        onSearch={handleSearch}
        totalPages={totalPages}
        currentPage={page}
        toPage={goToPage}
        setSelected={(items) => dispatch(setSelectedData(items))}
        filterCheckbox={
          !demo && (
            <RootCheckbox
              onChange={() => handleCheckFilter()}
              label={t("page.products.checkbox_filter")}
              checked={inactiveData}
            />
          )
        }
      />
    </ScreenLayout>
  );
};
